/**
 * Continuum phase for gating citizen CLAW actions.
 * intake → draft (LNP created) → submitted (filed) → agency progress.
 */

const POST_SUBMIT_STATUSES = new Set([
  'submitted',
  'under_review',
  'in_review',
  'review',
  'plan_check',
  'inspection',
  'inspections',
  'approved',
  'issued',
  'complete',
  'completed',
]);

/** @typedef {'intake' | 'draft' | 'submitted'} ContinuumPhase */

/**
 * @param {{ session?: object, applicationStatus?: object|string|null }} args
 * @returns {ContinuumPhase}
 */
export function resolveContinuumPhase({ session = null, applicationStatus = null } = {}) {
  const statusRaw = typeof applicationStatus === 'string'
    ? applicationStatus
    : (applicationStatus?.status || session?.lnpStatus || session?.applicationStatus?.status || '');
  const status = String(statusRaw || '').trim().toLowerCase().replace(/\s+/g, '_');
  const phase = String(session?.phase || '').trim().toLowerCase();

  if (
    phase === 'submitted'
    || POST_SUBMIT_STATUSES.has(status)
    || status.includes('submit')
  ) {
    return 'submitted';
  }

  const hasLnp = Boolean(
    session?.publicTrackingCode
    || session?.lnpApplicationId
    || session?.publicAppUrl
    || status === 'draft'
    || phase === 'ready',
  );
  if (hasLnp) return 'draft';
  return 'intake';
}

/** Actions available from intake onward (pre-filing coaches). */
const INTAKE_ACTIONS = new Set([
  'completeness',
  'fees',
  'counter',
  'zoning',
  'portfolio',
]);

/** Need an LNP draft (tracking code) first. */
const DRAFT_ACTIONS = new Set([
  'status',
]);

/** Only after the application is filed. */
const SUBMITTED_ACTIONS = new Set([
  'inspectSuggest',
  'inspectPrep',
  'inspectAssign',
  'corrections',
]);

/**
 * @param {string} action
 * @param {ContinuumPhase} phase
 * @param {{ mode?: string }} [opts]
 */
export function civicActionAllowed(action, phase, opts = {}) {
  const key = String(action || '');
  const mode = opts.mode || 'citizen';
  // Roster assign is staff-only — citizens can prep checklists after filing.
  if ((key === 'inspectSuggest' || key === 'inspectAssign') && mode !== 'staff') {
    return false;
  }
  if (INTAKE_ACTIONS.has(key)) return true;
  if (DRAFT_ACTIONS.has(key)) return phase === 'draft' || phase === 'submitted';
  if (SUBMITTED_ACTIONS.has(key)) {
    // Staff may prep correction plans once a draft exists; inspections still require submit.
    if (mode === 'staff' && key === 'corrections') {
      return phase === 'draft' || phase === 'submitted';
    }
    // Staff may suggest/assign once drafted or submitted (field desk).
    if (mode === 'staff' && (key === 'inspectSuggest' || key === 'inspectAssign')) {
      return phase === 'draft' || phase === 'submitted';
    }
    return phase === 'submitted';
  }
  return true;
}

export function continuumPhaseLabel(phase) {
  if (phase === 'submitted') {
    return 'Application submitted — you can prepare for inspection and handle corrections';
  }
  if (phase === 'draft') {
    return 'Draft saved — submit when ready; inspection help unlocks after filing';
  }
  return 'Finish your answers, then start a draft and submit before inspection scheduling';
}
